import React, { useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import { css, cx } from "@emotion/css";
import "keen-slider/keen-slider.min.css";
import { EventCard } from "../event-card/event-card";
import type { Event } from "../../../../api/services/events";

interface EventCarouselProps {
  events: Event[];
  className?: string;
}

const carouselWrapper = css({
  position: "relative",
  width: "100%",
});

const slider = css({
  width: "100%",
  overflow: "visible",
  overflowY: "hidden",
  "@media (max-width: 768px)": {
    overflowY: "unset",
  },
});

const eventCard = css({
  overflow: "visible",
  minWidth: "290px",
  maxWidth: "290px",
  "@media (max-width: 768px)": {
    minWidth: "250px",
    maxWidth: "250px",
  },
  "@media (max-width: 480px)": {
    minWidth: "230px",
    maxWidth: "230px",
  },
});

const arrow = css({
  width: "36px",
  height: "36px",
  position: "absolute",
  top: "36%",
  transform: "translateY(-50%)",
  fill: "var(--primary-color)",
  cursor: "pointer",
  padding: "7px",
  borderRadius: "50%",
  backgroundColor: "rgba(var(--pcrgba-600), 0.7)",
  zIndex: 10,
  border: "none",
  ":hover": {
    backgroundColor: "rgba(var(--pcrgba-500), 0.8)",
  },
  "@media (max-width: 920px)": {
    top: "33%",
  },
  "@media (max-width: 768px)": {
    display: "none",
  },
});

const arrowLeft = css({
  left: "-24px",
});

const arrowRight = css({
  right: "16px",
});

const arrowDisabled = css({
  opacity: 0.4,
  cursor: "default",
  ":hover": {
    backgroundColor: "rgba(var(--pcrgba-600), 0.7)",
  },
});

const Arrow: React.FC<{
  left?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ left, disabled, onClick }) => (
  <button
    className={cx(
      arrow,
      left ? arrowLeft : arrowRight,
      disabled && arrowDisabled
    )}
    onClick={onClick}
    disabled={disabled}
    aria-label={left ? "Anterior" : "Próximo"}
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      {left ? (
        <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
      ) : (
        <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
      )}
    </svg>
  </button>
);

export const EventCarousel: React.FC<EventCarouselProps> = ({
  events,
  className,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider({
    slides: {
      perView: "auto",
      spacing: 16,
    },
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  return (
    <div className={cx(carouselWrapper, className)}>
      <div
        ref={sliderRef}
        className={cx(slider, "keen-slider")}
        style={{ overflowY: "unset", overflowX: "unset" }}
      >
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            className={cx(eventCard, "keen-slider__slide")}
            style={{ overflow: "visible" }}
          />
        ))}
      </div>

      {loaded && instanceRef.current && (
        <>
          <Arrow
            left
            onClick={() => instanceRef.current?.prev()}
            disabled={currentSlide === 0}
          />
          <Arrow
            onClick={() => instanceRef.current?.next()}
            disabled={
              currentSlide ===
              (instanceRef.current?.track?.details?.slides?.length || 0) - 1
            }
          />
        </>
      )}
    </div>
  );
};

export default EventCarousel;
